import { anmeldekopf, basisUrl, rufe } from './client';

/**
 * Die Trainingsplanung (TAG-03, TAG-09, AeA-08).
 *
 * Alle Uhrzeiten stehen als `HH:MM` in Ortszeit — der Tagesplan wird in
 * Ortszeit geführt (FRS-10).
 */

/**
 * Ein Trainingslauf: ein Produkt zu einer Anfangszeit.
 *
 * Drei gleichzeitige Trainings desselben Produkts sind drei Termine, die
 * gegeneinander rotieren (E-38, TAG-07) — sie gehören in EINE Zeile, sonst
 * stünde dasselbe Training dreimal da.
 */
export type Trainingszeile = {
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly produkt: string;
  readonly beginn: string;
  readonly ende: string;
  /**
   * In welchem Block dieses Training anfängt.
   *
   * `null` heißt: Sein Beginn fällt auf keinen Block dieses Tages. Ein
   * solches Training lässt sich nicht einplanen — das gehört in die Liste und
   * nicht in eine Fehlermeldung beim Anwenden.
   */
  readonly startblock: number | null;
  /**
   * Wie viele Bausteine die Trainingsdefinition führt (PRD-29).
   *
   * SEIT AeA-11 IST SIE DIE VORLAGE: Der Ablauf folgt aus den Bausteinen und
   * ihren Phasen. Null heißt, dass es nichts anzuordnen gibt — der einzige
   * Grund, aus dem sich ein Training nicht anwenden lässt.
   */
  readonly bausteine: number;
  /**
   * TAG-20: was am abgeleiteten Ablauf auffällt — Hinweise, keine Hindernisse.
   *
   * Etwa eine Fahrmodulphase mit weniger Stationen als Terminen: Zwei Termine
   * stehen dann zugleich an derselben Übung. Ob das ein Konflikt ist,
   * entscheidet die Ressource und nicht der Baustein.
   */
  readonly ablaufBefunde: readonly string[];
  /** Wie viele Anfangszeiten dieses Produkt an diesem Tag hat. */
  readonly laeufe: number;
};

export type Tagesplanzeile = {
  readonly block: number;
  readonly beginn: string;
  readonly ende: string;
  readonly blockAbweichend: boolean;
  readonly termin: string;
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly produkt: string;
  readonly terminbeginn: string;
  /** BUC-66: `offen` (Einzelplatzverkauf) oder `exklusiv` (einem Kunden ganz). */
  readonly terminart: string;
  /** Der Kunde eines Exklusivtermins; `null` beim offenen Termin (BUC-67). */
  readonly kundenname: string | null;
  /** `null` heißt: leer — hier ist noch Platz. */
  readonly aktivitaetsart: string | null;
  readonly baustein: string | null;
  readonly bausteinname: string | null;
  readonly abweichend: boolean | null;
};

/**
 * Eine Belegung: was ein Baustein bei einem Termin am Stück einnimmt (TAG-08).
 *
 * Nicht die Zelle — eine 45-Minuten-Übung belegt drei Blöcke, und wer einen
 * davon allein verschiebt, zerreißt sie in der Mitte.
 *
 * Sie hängt seit AeA-09 am TERMIN: Eine Gruppe darunter gibt es nicht.
 */
export type Belegung = {
  readonly belegung: string;
  readonly termin: string;
  readonly artikel: string;
  readonly produkt: string;
  readonly artikelnummer: string;
  readonly terminbeginn: string;
  readonly aktivitaetsart: string;
  readonly baustein: string | null;
  readonly bausteinname: string | null;
  readonly vonBlock: number;
  readonly bisBlock: number;
  readonly laenge: number;
  readonly vonZeit: string;
  readonly bisZeit: string;
  /** Die Phase aus der Trainingsdefinition; `null`, wenn das Produkt sie nicht führt. */
  readonly phase: number | null;
  readonly abweichend: boolean;
};

/**
 * Was einen Termin den ganzen Tag begleitet (TAG-15, TAG-71).
 *
 * „Ein Trainer wird nicht einer Ressource zugeordnet, sondern ein Trainer
 * wird einem Termin zugeordnet." Was zu einer einzelnen Übung gehört, hängt
 * dagegen an der Belegung.
 *
 * Gelesen aus `fach.ressourcenbelegung`; geschrieben wird dort über den Weg,
 * den Kapitel 12 dafür hat (`POST /ressourcen/belegen`).
 */
export type Terminbelegung = {
  readonly termin: string;
  readonly belegung: string;
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: string;
  readonly status: string;
  /** RES-89: erst eine harte Belegung deckt den Bedarf. */
  readonly hart: boolean;
  /**
   * RES-33: Eine laufende Anfrage mit Frist — noch keine Zusage.
   *
   * Der Status allein sagt es nicht: Die Zuteilung aus der Tagesplanung
   * (TAG-82) und die Anfrage tragen beide `vorlaeufig`. Die Unterscheidung
   * rechnet die Datenbank (0173).
   */
  readonly angefragt: boolean;
  /** Wann die Anfrage verfaellt, als ISO-Zeitpunkt — `null` ohne Anfrage. */
  readonly frist: string | null;
  readonly vonZeit: string;
  readonly bisZeit: string;
};

/**
 * Was ein Termin braucht und was davon belegt ist (TAG-15, RES-24).
 *
 * Die Menge kommt aus der Bedarfsstaffel — „ab n Teilnehmern brauchst du m
 * davon" — und ist damit der Betreuungsschlüssel aus TAG-15.
 */
export type Terminbedarf = {
  readonly termin: string;
  readonly bedarf: string;
  readonly bedarfsname: string;
  readonly ressourcenart: string | null;
  readonly verlangt: number;
  readonly gedeckt: number;
  readonly zwingend: boolean;
};

/** Ein Baustein, der sich an diesem Tag setzen lässt (PRD-29). */
export type Setzbar = {
  readonly artikel: string;
  readonly baustein: string;
  readonly bezeichnung: string;
  readonly art: string;
  readonly dauerBloecke: number;
  readonly phase: number | null;
};

/** Eine Ressource an einer Belegung (TAG-03, DAT-14). */
export type Belegungsressource = {
  readonly belegung: string;
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: string;
  readonly rolle: string;
  readonly anzahl: number;
  readonly status: string;
  readonly platzzahl: number | null;
};

/**
 * Ein Befund am Tagesplan (TAG-13, TAG-24).
 *
 * `zwingend` verhindert die FREIGABE und nicht die Bearbeitung — TAG-20 ist
 * da eindeutig. Die endgültige Zuordnung jeder Regel ist offener Punkt 41.
 */
export type Konflikt = {
  readonly art: string;
  readonly schwere: 'zwingend' | 'hinweis';
  readonly block: number;
  readonly ressource: string | null;
  readonly befund: string;
  /**
   * TAG-13: Welchen Termin der Befund meint — `null`, wenn mehrere.
   *
   * Der Text nennt Produkt und Uhrzeit, und bei zwei Gruppen desselben
   * Trainings zur selben Zeit (E-38) steht derselbe Satz zweimal da. Eine
   * doppelt belegte Ressource oder die Pausenregel eines Trainers betrifft
   * dagegen mehrere Termine (0175).
   */
  readonly termin: string | null;
};

/**
 * Was eine Belegung braucht und was davon steht (PRD-29).
 *
 * „Bei der Tagesplanung werden Ressourcen und keine Bausteine geplant. Die
 * Ressourcen müssen auf Basis der Bausteine geplant werden." Diese Zeile ist
 * diese Basis: Der Bedarf kommt vom Baustein, die Zuteilung ist die Arbeit
 * des Tages.
 */
export type Bedarfsdeckung = {
  readonly belegung: string;
  readonly termin: string;
  readonly baustein: string;
  readonly bausteinname: string;
  readonly bedarf: string;
  readonly bedarfsname: string;
  readonly ressourcenart: string | null;
  readonly verlangt: number;
  readonly zugeteilt: number;
  readonly zwingend: boolean;
  /** TAG-23: Rüstzeit — sie steht am Bedarf und nicht am Tagesplan. */
  readonly vorbereitungMinuten: number;
  readonly nachbereitungMinuten: number;
  /**
   * PRD-29: WELCHE Ressourcen diesen Bedarf decken.
   *
   * `zugeteilt` sagt nur, wie viele es sind. Die Maske filterte vorher nach
   * der Ressourcenart — und bei einem Bedarf über eine Eigenschaft („das
   * Bistro", ÄA-15) ist die Art leer. Die Ressource stand dann am Balken,
   * aber nicht in der Bedarfszeile (0175).
   */
  readonly ressourcen: readonly string[];
};

/** Eine Ressource, die zu einer Belegung passt (AeA-05, AeA-06). */
export type Ressourcenvorschlag = {
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: string;
  readonly status: string;
  readonly platzzahl: number | null;
  /** `null` heißt: keine Eignung gepflegt (gilt für alles außer Modulen). */
  readonly eignungsgrad: string | null;
  readonly gruppengleichzeitig: number | null;
  /** PRD-29: Deckt sie den Bedarf, nach dem gefragt wurde? */
  readonly decktBedarf: boolean;
  readonly belegtImZeitraum: boolean;
  readonly gesperrt: boolean;
};

export type Tagesplan = {
  readonly id: string;
  readonly standort: string;
  readonly standortname: string;
  readonly tag: string;
  readonly blocklaengeMinuten: number;
  readonly tagesbeginn: string;
  readonly tagesende: string;
  readonly freigegeben: boolean;
  /** TAG-33: Entwurf, freigegeben, in Durchführung, abgeschlossen. */
  readonly planungsstand: Planungsstand;
  /**
   * TAG-30, ERR-13: der Stand, auf dem dieser Bildschirm arbeitet.
   *
   * Er geht bei JEDEM Handgriff zurück an den Server. Hat jemand anders
   * zwischenzeitlich geschrieben, wird der Handgriff abgewiesen statt die
   * fremde Änderung zu überfahren.
   */
  readonly fassung: string;
  /** TAG-24: Was der Freigabe im Weg steht — `null`, wenn nichts. */
  readonly freigabeHindernis: string | null;
  readonly trainings: readonly Trainingszeile[];
  readonly raster: readonly Tagesplanzeile[];
  /** TAG-08: der Tag als Belegungen — das, was sich anfassen lässt. */
  readonly belegungen: readonly Belegung[];
  /** PRD-29: die Bausteine der Produkte dieses Tages. */
  readonly setzbar: readonly Setzbar[];
  /** TAG-03, DAT-14: wer und was an den Belegungen steht. */
  readonly ressourcen: readonly Belegungsressource[];
  /** TAG-13: die Befunde, nach TAG-24 in zwingend und Hinweis geschieden. */
  readonly konflikte: readonly Konflikt[];
  /** PRD-29: was jede Übung braucht und was davon zugeteilt ist. */
  readonly deckung: readonly Bedarfsdeckung[];
  /** TAG-15, TAG-71: wer den ganzen Termin begleitet — vor allem der Trainer. */
  readonly terminbelegungen: readonly Terminbelegung[];
  /** TAG-15, RES-24: was jeder Termin braucht und was davon belegt ist. */
  readonly terminbedarf: readonly Terminbedarf[];
  readonly hinweise: readonly {
    readonly art: string;
    readonly termin: string;
    readonly block: number;
    readonly hinweis: string;
  }[];
  /**
   * Was die Trainer zu ihren Einsätzen gemeldet haben (RES-77, TAG-43).
   *
   * SIE KOMMEN MIT DEM PLAN, damit der Planer sieht, was vom Tag zurückkam. Auf
   * das Honorar wirken sie in der Abrechnungsgrundlage (RES-76, seit 0249).
   * Eine Rückmeldung, die der Trainer abends schreibt und die niemand sieht,
   * ist keine.
   */
  readonly rueckmeldungen: readonly Planrueckmeldung[];
};

export type Planrueckmeldung = {
  readonly termin: string;
  readonly belegung: string;
  /** Wer gemeldet hat — der Trainer, nicht das Benutzerkonto. */
  readonly ressourcenname: string;
  readonly art: string;
  /** HH:MM in Ortszeit; `null` außer bei verkürzt und verlängert. */
  readonly vonZeit: string | null;
  readonly bisZeit: string | null;
  readonly bemerkung: string | null;
  readonly gemeldetAm: string;
};

/** Wie die Arten am Bildschirm heißen — dieselben Worte wie im Trainerbereich. */
export const RUECKMELDUNGSNAME: Record<string, string> = {
  wie_beauftragt: 'lief wie geplant',
  verkuerzt: 'kürzer als geplant',
  verlaengert: 'länger als geplant',
  ausgefallen: 'fiel aus',
  vertreten: 'jemand anders ist gefahren',
};

/** TAG-33: die vier Stände eines Betriebstags. */
export const PLANUNGSSTAENDE = [
  'entwurf',
  'freigegeben',
  'in_durchfuehrung',
  'abgeschlossen',
] as const;

export type Planungsstand = (typeof PLANUNGSSTAENDE)[number];

/** Wie der Stand in der Maske heißt — ein Ding, ein Name (TAG-33). */
export const STANDNAME: Readonly<Record<Planungsstand, string>> = {
  entwurf: 'in Planung',
  freigegeben: 'freigegeben',
  in_durchfuehrung: 'in Durchführung',
  abgeschlossen: 'abgeschlossen',
};

export type Tageszeile = {
  readonly id: string;
  readonly tag: string;
  /**
   * Was an diesem Tag stattfindet.
   *
   * Daneben stand bis zum 09.09.2026 die Zahl der LÄUFE — ein Produkt zu
   * einer Anfangszeit. Der Unterschied beider Zahlen war die Auskunft „drei
   * gleiche oder drei verschiedene Trainings"; der Auftraggeber hat sie als
   * für die Planung unerheblich gestrichen.
   */
  readonly termineGesamt: number;
  readonly freigegeben: boolean;
  /** TAG-33: derselbe Stand wie am Tagesplan — die Liste zeigt ihn mit. */
  readonly planungsstand: Planungsstand;
};

export type Planungsstandort = {
  readonly kennung: string;
  readonly bezeichnung: string;
  /** Wie viele Betriebstage es hier überhaupt gibt — ohne einen ist nichts zu planen. */
  readonly betriebstage: number;
  /**
   * Der Standort, mit dem die Planung aufgeht (0147, SYS-10).
   *
   * Höchstens einer trägt ihn, keiner ist zulässig. Welcher es ist, ist
   * Stammdatum und keine Programmentscheidung — deshalb kommt es von hier
   * und steht nicht als Schlüssel in der Maske.
   */
  readonly hauptstandort: boolean;
};

export async function standorte(): Promise<{
  readonly standorte: readonly Planungsstandort[];
}> {
  return rufe('/tagesplanung/standorte');
}

export async function tage(
  standort: string,
  von: string,
  bis: string,
): Promise<{ readonly tage: readonly Tageszeile[] }> {
  const p = new URLSearchParams({ standort, von, bis });
  return rufe(`/tagesplanung/tage?${p.toString()}`);
}

/**
 * Ein Standort in der Übersicht der offenen Planungen (TAG-33, Auftrag CRM-68).
 *
 * Offen ist ein Tag mit Terminen, der noch „in Planung" steht — das zählt die
 * API, nicht die Maske. `fruehesterTag` ist `null`, wenn nichts offen ist.
 */
export type OffeneTageJeStandort = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly offeneTage: number;
  readonly fruehesterTag: string | null;
};

export async function offeneTage(
  von: string,
  bis: string,
): Promise<{ readonly standorte: readonly OffeneTageJeStandort[] }> {
  const p = new URLSearchParams({ von, bis });
  return rufe(`/tagesplanung/offene-tage?${p.toString()}`);
}

/**
 * Der Tagesplan — wahlweise OHNE einzelne Termine (TAG-13, TAG-20).
 *
 * Auftraggeber, 13.09.2026: „in der trainingsplanung soll grundsätzlich die
 * möglichkeit bestehen, jeden termin an und abzuwählen und von der planung
 * auszuschließen, damit kann einfach getestet werden, ob die ressourcen
 * reichen."
 *
 * Die Probe rechnet der SERVER. Die harten Befunde des Tages — „das Modul ist
 * zwei Terminen zugeteilt", „das Bistro fasst 40, eingeplant sind 52" —
 * gehören dem Tag und keinem Termin; eine Maske, die Zeilen ausblendet, kann
 * sie nicht neu rechnen.
 */
export async function tagesplan(id: string, ohne: readonly string[] = []): Promise<Tagesplan> {
  const pfad = `/tagesplanung/${encodeURIComponent(id)}`;
  return rufe(ohne.length === 0 ? pfad : `${pfad}?ohne=${ohne.join(',')}`);
}

/**
 * TAG-07 i.d.F. AeA-11: den Ablauf eines Programms auf den Tag legen.
 *
 * Übergeben wird der ARTIKEL und kein Schema mehr — „der Ablauf eines
 * Trainings definiert sich aus den Bausteinen und den Phasen".
 *
 * `ersetzen` ist ausdrücklich: Ein Anwenden, das einen von Hand
 * nachbearbeiteten Tag stillschweigend überschreibt, nimmt eine Arbeit weg,
 * die niemand mehr sieht.
 */
/*
 * ============================================================================
 * JEDER SCHREIBENDE WEG TRÄGT DIE FASSUNG (TAG-30, ERR-13)
 * ============================================================================
 *
 * Sie ist der Stand, den dieser Bildschirm gesehen hat. Ohne sie wüsste der
 * Server nicht, ob der Planer den Tag meint, der gerade dasteht — und zwei
 * Planer überschrieben sich lautlos. Die Maske reicht `plan.fassung` durch;
 * sie denkt sich den Wert nicht aus.
 */
export async function ablaufAnwenden(
  betriebstag: string,
  artikel: string,
  ersetzen: boolean,
  fassung: string,
): Promise<{ readonly eintraege: number }> {
  return rufe(`/tagesplanung/${encodeURIComponent(betriebstag)}/ablauf-anwenden`, {
    verfahren: 'POST',
    rumpf: { artikel, ersetzen, fassung },
  });
}

/**
 * TAG-33: den Planungsstand setzen — Freigabe, Rücknahme, Durchführung.
 *
 * Ein Weg für alle vier Stände: Welche Übergänge es gibt, entscheidet die
 * Datenbank. Verlangt wird `tagesplan/freigeben` (BEN-02) und nicht `planen` —
 * die Freigabe ist die Zusage, dass der Tag so läuft.
 */
export async function standSetzen(
  betriebstag: string,
  stand: Planungsstand,
  fassung: string,
): Promise<{ readonly stand: Planungsstand }> {
  return rufe(`/tagesplanung/${encodeURIComponent(betriebstag)}/stand`, {
    verfahren: 'POST',
    rumpf: { stand, fassung },
  });
}

/*
 * ============================================================================
 * DIE NACHBEARBEITUNG (TAG-08)
 * ============================================================================
 *
 * „Ein aus einer Vorlage erzeugter Plan ist FREI nachbearbeitbar." Vier
 * Handgriffe: verschieben, tauschen, setzen, leeren. Was unmöglich ist,
 * weist die Datenbank mit einer Meldung ab, die den Grund nennt; was nur
 * ungewöhnlich ist, erscheint als Hinweis am Tag (TAG-13, TAG-20).
 */

export async function verschieben(
  betriebstag: string,
  belegung: string,
  block: number,
  fassung: string,
): Promise<{ readonly bloecke: number }> {
  return rufe(
    `/tagesplanung/${encodeURIComponent(betriebstag)}/belegungen/${encodeURIComponent(belegung)}/verschieben`,
    { verfahren: 'POST', rumpf: { block, fassung } },
  );
}

export async function tauschen(
  betriebstag: string,
  a: string,
  b: string,
  fassung: string,
): Promise<{ readonly bloecke: number }> {
  return rufe(`/tagesplanung/${encodeURIComponent(betriebstag)}/tauschen`, {
    verfahren: 'POST',
    rumpf: { a, b, fassung },
  });
}

export async function setzen(
  betriebstag: string,
  termin: string,
  block: number,
  baustein: string,
  fassung: string,
): Promise<{ readonly belegung: string }> {
  return rufe(`/tagesplanung/${encodeURIComponent(betriebstag)}/belegungen`, {
    verfahren: 'POST',
    rumpf: { termin, block, baustein, fassung },
  });
}

/*
 * DIE FASSUNG IN DER ADRESSE UND NICHT IM RUMPF.
 *
 * Ein DELETE mit Rumpf ist quer: Zwischenspeicher und Gegenstellen behandeln
 * ihn unterschiedlich. Der Stand gehört damit in die Abfrage — dieselbe
 * Entscheidung wie im Controller.
 */
export async function leeren(
  betriebstag: string,
  belegung: string,
  fassung: string,
): Promise<{ readonly bloecke: number }> {
  return rufe(
    `/tagesplanung/${encodeURIComponent(betriebstag)}/belegungen/${encodeURIComponent(belegung)}`
      + `?fassung=${encodeURIComponent(fassung)}`,
    { verfahren: 'DELETE' },
  );
}

/*
 * ============================================================================
 * DIE RESSOURCEN AM BLOCK (TAG-03, DAT-14)
 * ============================================================================
 */

export async function ressourcenvorschlag(
  betriebstag: string,
  belegung: string,
  bedarf?: string,
): Promise<{ readonly vorschlaege: readonly Ressourcenvorschlag[] }> {
  const p = bedarf === undefined ? '' : `?bedarf=${encodeURIComponent(bedarf)}`;
  return rufe(
    `/tagesplanung/${encodeURIComponent(betriebstag)}/belegungen/${encodeURIComponent(belegung)}/ressourcenvorschlag${p}`,
  );
}

export async function ressourceSetzen(
  betriebstag: string,
  belegung: string,
  ressource: string,
  rolle: string,
  fassung: string,
  anzahl = 1,
): Promise<{ readonly bloecke: number }> {
  return rufe(
    `/tagesplanung/${encodeURIComponent(betriebstag)}/belegungen/${encodeURIComponent(belegung)}/ressourcen`,
    { verfahren: 'POST', rumpf: { ressource, rolle, anzahl, fassung } },
  );
}

/**
 * Was die automatische Zuteilung getan oder nicht getan hat (TAG-82).
 *
 * Jede Zeile ist eine Zuteilung oder eine Lücke mit Grund. Ein Automat,
 * dessen Ergebnis man suchen muss, ist keine Hilfe — und was er NICHT
 * unterbringen konnte, stünde nirgends, wenn es hier fehlte.
 */
export type Zuteilung = {
  /**
   * `staffelung` seit ÄA-17: Bevor die Zuteilung sucht, dreht sie die
   * Stationen einer Phase je Termin gegeneinander — sonst verlangen drei
   * gleichzeitige Gruppen dasselbe Modul zur selben Zeit.
   */
  readonly ebene: 'staffelung' | 'uebung' | 'termin';
  readonly termin: string;
  /** `null` auf den Ebenen `termin` und `staffelung` — dort gibt es keine einzelne Übung. */
  readonly belegung: string | null;
  readonly gegenstand: string;
  readonly zeit: string;
  /** `null` bei einer Staffelung: Sie deckt keinen Bedarf, sie macht Platz. */
  readonly bedarf: string | null;
  readonly bedarfsname: string;
  readonly ressource: string | null;
  readonly ressourcenname: string | null;
  readonly ergebnis: 'gestaffelt' | 'zugeteilt' | 'offen';
  readonly grund: string | null;
};

/**
 * TAG-82: die Ressourcen des ganzen Tages auf einmal zuteilen.
 *
 * Ohne Rumpf, weil der Aufruf genau eine Bedeutung hat: decke, was sich
 * decken lässt. Er überschreibt nichts, was von Hand steht, und erzeugt
 * keinen zwingenden Konflikt nach TAG-24.
 */
export async function ressourcenZuteilen(
  betriebstag: string,
  fassung: string,
): Promise<{ readonly zuteilungen: readonly Zuteilung[] }> {
  return rufe(`/tagesplanung/${encodeURIComponent(betriebstag)}/ressourcen-zuteilen`, {
    verfahren: 'POST',
    rumpf: { fassung },
  });
}

export async function ressourceEntfernen(
  betriebstag: string,
  belegung: string,
  ressource: string,
  fassung: string,
): Promise<{ readonly bloecke: number }> {
  return rufe(
    `/tagesplanung/${encodeURIComponent(betriebstag)}/belegungen/${encodeURIComponent(belegung)}/ressourcen/${encodeURIComponent(ressource)}`
      + `?fassung=${encodeURIComponent(fassung)}`,
    { verfahren: 'DELETE' },
  );
}

/**
 * Der Aushang des Tages als PDF (TAG-35).
 *
 * NICHT über `rufe`: Die Antwort ist kein JSON, sondern das Dokument selbst
 * — derselbe Weg wie bei der Belegausgabe.
 */
export async function aushang(
  betriebstag: string,
): Promise<{ readonly blob: Blob; readonly dateiname: string }> {
  return dokument(`/tagesplanung/${betriebstag}/aushang`, 'tagesplan.pdf');
}

/** TAG-35: ein Blatt je Trainer — der zweite der drei Ausdrucke. */
export async function trainerplan(
  betriebstag: string,
): Promise<{ readonly blob: Blob; readonly dateiname: string }> {
  return dokument(`/tagesplanung/${betriebstag}/trainerplan`, 'trainerplan.pdf');
}

/** Ein PDF vom Server — derselbe Weg wie bei der Belegausgabe. */
async function dokument(
  pfad: string,
  rueckfallname: string,
): Promise<{ readonly blob: Blob; readonly dateiname: string }> {
  const antwort = await fetch(`${basisUrl()}${pfad}`, {
    headers: anmeldekopf(),
  });

  if (!antwort.ok) {
    /*
     * Der Fehler kommt als JSON und wird HIER in dieselbe Form gebracht wie
     * bei `rufe`. Der häufigste ist kein Programmfehler, sondern eine
     * Auskunft: Der Setzdienst steht in einem eigenen Container, und wenn er
     * gerade nicht kann, lässt sich der Tag weiter planen — nur eben nicht
     * drucken (ERR-08).
     */
    let rumpf: unknown;
    try {
      rumpf = await antwort.json();
    } catch {
      rumpf = {
        kennung: 'unbekannt',
        meldung: { wasGeschah: 'Der Aushang konnte nicht gesetzt werden.' },
      };
    }
    throw Object.assign(new Error('Aushang fehlgeschlagen'), {
      status: antwort.status,
      ...(rumpf as object),
    });
  }

  const kopf = antwort.headers.get('content-disposition') ?? '';
  return {
    blob: await antwort.blob(),
    dateiname: /filename="([^"]+)"/.exec(kopf)?.[1] ?? rueckfallname,
  };
}
